import ResourceSvc from '@/services/resource.svc.js';
import { Table, TableColumn, Select, Option, Input } from 'element-ui';
import { Pagination as LPagination } from '@/components/index';

const resourceService = new ResourceSvc();

const PER_PAGE_OPTIONS = [5, 10, 25, 50];
const CLUB_STATUSES = [1, 2, 4, 5, 6]; // Active, Inactive, Temporary(Not Paid), Historical, Review

function getStatusFilter(query) {
    const status = query.status != null ? Number(query.status) : null;
    return CLUB_STATUSES.indexOf(status) >= 0 ? status : null;
}

function getRequestedMembershipTypesFilter(query) {
    if (query.hasRequestedMembershipTypes === 'true') return true;
    if (query.hasRequestedMembershipTypes === 'false') return false;
    return null;
}

export default {
    name: 'ManageClubs',

    components: {
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Select.name]: Select,
        [Option.name]: Option,
        [Input.name]: Input,
        LPagination
    },

    data() {
        const query = (this.$route && this.$route.query) || {};

        return {
            isLoading: false,
            isExporting: false,
            clubs: [],
            total: 0,
            page: 1,
            perPage: 10,
            perPageOptions: PER_PAGE_OPTIONS,
            filterName: '',
            filterYear: 2026,
            filterStatus: getStatusFilter(query),
            filterHasRequested: getRequestedMembershipTypesFilter(query),
            years: [],
            statuses: [
                { label: 'Any', value: null },
                { label: 'Active', value: 1 },
                { label: 'Inactive', value: 2 },
                { label: 'Not Paid', value: 4 },
                { label: 'Historical', value: 5 },
                { label: 'Review', value: 6 }
            ],
            requestedOptions: [
                { label: 'Any', value: null },
                { label: 'Yes', value: true },
                { label: 'No', value: false }
            ]
        };
    },

    computed: {
        skip() {
            return (this.page - 1) * this.perPage;
        },
        from() {
            return this.total > 0 ? this.skip + 1 : 0;
        },
        to() {
            return Math.min(this.skip + this.perPage, this.total);
        }
    },

    watch: {
        perPage() {
            this.resetPageAndLoad();
        },
        filterYear() {
            this.resetPageAndLoad();
        },
        filterStatus() {
            this.resetPageAndLoad();
        },
        filterHasRequested() {
            this.resetPageAndLoad();
        }
    },

    methods: {
        onPageChange(newPage) {
            this.page = newPage;
            this.load();
        },

        resetPageAndLoad() {
            this.page = 1;
            this.load();
        },

        searchByName() {
            this.page = 1;
            this.load();
        },

        createNewClub() {
            this.$router.push('/admin/club/new');
        },

        exportClubs() {
            this.isExporting = true;

            let query = `?clubName=${encodeURIComponent(this.filterName || '')}`;
            if (this.filterYear != null) query += `&year=${this.filterYear}`;
            if (this.filterStatus != null) query += `&status=${this.filterStatus}`;
            if (this.filterHasRequested != null) query += `&hasRequestedMembershipTypes=${this.filterHasRequested}`;

            const url = '/api/Admin/Club/ExportClubs' + query;

            fetch(url, { credentials: 'same-origin' })
                .then(response => {
                    if (!response.ok) throw new Error('Export failed');
                    const disposition = response.headers.get('content-disposition') || '';
                    const fileName = this.getExportFileName(disposition);
                    if (!fileName) throw new Error('Export filename is missing');

                    return response.blob().then(blob => ({ blob, fileName }));
                })
                .then(({ blob, fileName }) => {
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = fileName;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(link.href);
                })
                .catch(() => {
                    this.$showError('Export failed');
                })
                .finally(() => {
                    this.isExporting = false;
                });
        },

        getExportFileName(disposition) {
            const encodedFileNameMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
            if (encodedFileNameMatch && encodedFileNameMatch[1]) {
                return decodeURIComponent(encodedFileNameMatch[1].trim());
            }

            const fileNameMatch = disposition.match(/filename="?([^";]+)"?/i);
            return fileNameMatch && fileNameMatch[1]
                ? fileNameMatch[1].trim()
                : null;
        },

        rowClick(row) {
            this.$router.push({
                path: '/admin/club/' + row.id,
                query: row.year ? { year: row.year } : {}
            });
        },

        async load() {
            this.isLoading = true;
            try {
                const isNeedSupportData = !this.years.length;
                let query = `?clubName=${encodeURIComponent(this.filterName || '')}` +
                    `&options.skip=${this.skip}` +
                    `&options.take=${this.perPage}`;

                if (this.filterYear != null) query += `&year=${this.filterYear}`;
                if (this.filterStatus != null) query += `&status=${this.filterStatus}`;
                if (this.filterHasRequested != null) query += `&hasRequestedMembershipTypes=${this.filterHasRequested}`;

                const res = await resourceService.getAdminClubsList(query);
                if (res && res.body && res.body.isSuccess) {
                    this.clubs = res.body.outputData.items;
                    this.total = res.body.outputData.total;
                    if (isNeedSupportData) {
                        this.initYears(res.body.outputData.currentYear, res.body.outputData.isAvailableNextYearClubAffiliation);
                    }
                } else {
                    this.$showError(res && res.body && res.body.message ? res.body.message : null);
                }
            } catch (err) {
                this.$showError();
            } finally {
                if (!this._isDestroyed) this.isLoading = false;
            }
        },

        initYears(currentYear, isNextYear) {
            this.years = [{ label: 'Any', value: null }];
            for (let y = currentYear + (isNextYear ? 1 : 0); y >= 2015; y--) {
                this.years.push({ label: '' + y, value: y });
            }
        }
    },

    beforeDestroy() {
        this._isDestroyed = true;
    },

    mounted() {
        this.load();
    }
};



// WEBPACK FOOTER //
// ./src/components/admin/club/manage-clubs/manage-clubs.js